import { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Grid from '@mui/material/Unstable_Grid2';
import Link from '@mui/material/Link';
import LoadingButton from '@mui/lab/LoadingButton';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';

import { getCountries, getDeliveryTiers, getIndustries } from '../helper/Data';
import { useFormControls } from './SignupFormControls';
import { useTranslation } from 'react-i18next';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { setRecaptchaToken } from '../features/signup/signupSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getOtpValidated } from '../store';
import FormSelectField from './formInputs/SelectFieldComponent.js';
import FormTextField from './formInputs/TextFieldComponent';
import PhoneNumberField from './formInputs/PhoneNumberField';
import TermsAndConditionsCheckbox from './formInputs/CheckboxTermsAndConditions.js';
import { setSelectedVolumeTier } from '../features/signup/signupSlice';

import '../style/SignupForm.css';

const OtpForm = lazy(() => import('./OtpForm'));

const SignupForm = () => {
  const matches = useMediaQuery('(max-width:1200px)');
  const dispatch = useDispatch();
  const {
    getPhoneNumber, handleInputValue, handleCheckboxValue, handleDropdownValue, handleFormSubmit, formIsValid, errors, errorMessage, handleCreateSaleLead
  } = useFormControls();

  const [openOtp, setOpenOtp] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [buttonClass, setButtonClass] = useState("primary");

  const { t } = useTranslation();

  const [deliveryTiers, setDeliveryTiers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);

  const { token, otpValidated } = useSelector((store) => store.signup);

  const googlePoliciesBaseUrl = 'https://policies.google.com';
  const googlePrivacyUrl = `${googlePoliciesBaseUrl}/privacy`;
  const googleTosUrl = `${googlePoliciesBaseUrl}/terms`;

  const nameInputProps = { maxLength: 256 };
  const companyInputProps = { maxLength: 256 };
  const emailInputProps = { maxLength: 250 };
  const passwordInputProps = { maxLength: 256 };

  const onVerify = useCallback((token) => {
    dispatch(setRecaptchaToken({ token }));
    setTimeout(() => setRefreshReCaptcha(r => !r), 100000); // Refresh token after 100s
  }, [dispatch]);

  const toggleOtpDrawer = useCallback(() => {
    if (!openOtp) handleCreateSaleLead();

    if (getOtpValidated()) {
      handleFormSubmit(setRefreshReCaptcha);
    }

    setOpenOtp(!openOtp);
  }, [openOtp, handleCreateSaleLead, handleFormSubmit, setRefreshReCaptcha]);

  const handleTierListDropdownChange = (e) => {
    handleDropdownValue(e);
    dispatch( setSelectedVolumeTier(e.target.value) );
  }

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    toggleOtpDrawer();
  }, [toggleOtpDrawer]);

  const fetchData = useCallback(async () => {
    if (!countries.length || !deliveryTiers.length || !industries.length) {

      const [countriesData, deliveryTiersData, industriesData] = await Promise.all([
        getCountries(),
        getDeliveryTiers(),
        getIndustries(),
      ]);

      setCountries(countriesData);
      setDeliveryTiers(deliveryTiersData);
      setIndustries(industriesData);

    }
  }, [countries, deliveryTiers, industries]);


  useEffect(() => {
    if (errorMessage) {
      setButtonClass('error');
      const timeoutId = setTimeout(() => setButtonClass('primary'), 5000);
      return () => clearTimeout(timeoutId);
    }

    fetchData();
  }, [errorMessage, fetchData]);

  return (
    <Grid xs={12} sm={12} md={12} lg={12} className="right-side">

      <form className="form-body" onSubmit={(e) => handleSubmit(e)}>
        <Grid container spacing={1}>
          <FormTextField
            id="full-name-input"
            name="fullName"
            label="full_name"
            required
            type="text"
            inputProps={nameInputProps}
            errors={errors["fullName"]}
            onChange={handleInputValue}
            onBlur={handleInputValue}
          />
          <FormTextField
            id="company-name-input"
            name="companyName"
            label="company_name"
            required
            type="text"
            inputProps={companyInputProps}
            errors={errors["companyName"]}
            onChange={handleInputValue}
            onBlur={handleInputValue}
          />

          {deliveryTiers.length>0 && <FormSelectField
            id="volume-select"
            name="volume"
            label="deliveries_per_month"
            required
            defaultValue={undefined}
            menuItems={deliveryTiers}
            onChange={handleTierListDropdownChange}
            onBlur={handleTierListDropdownChange}
            hasTranslations={false}
            errors={errors["volume"]}
          />}

          {industries.length>0 && <FormSelectField
            id="industry-select"
            name="industry"
            label="industry"
            required={false}
            defaultValue="transport"
            menuItems={industries}
            onChange={handleDropdownValue}
            onBlur={handleDropdownValue}
            hasTranslations={true}
          />}

          <PhoneNumberField
            handleChangeCode={handleDropdownValue}
            handleChangePhone={handleInputValue}
            errors={errors["phoneNumber"]}
            placeholder='123456'
          />

          {countries.length>0 && <FormSelectField
            id="country-select"
            name="countryCode"
            label="country"
            required
            defaultValue="US"
            menuItems={countries}
            onChange={handleDropdownValue}
            onBlur={handleDropdownValue}
            hasTranslations={false}
          />}

          <FormTextField
            id="business-email-input"
            name="businessEmail"
            label="business_email"
            required
            type="email"
            inputProps={emailInputProps}
            errors={errors["businessEmail"]}
            onChange={handleInputValue}
            onBlur={handleInputValue}
          />

          <FormTextField
            id="password-input"
            name="password"
            label="password"
            required
            type="password"
            inputProps={passwordInputProps}
            errors={errors["password"]}
            onChange={handleInputValue}
            onBlur={handleInputValue}
          />

          <Grid xs={12} className="pre-signup">

            <TermsAndConditionsCheckbox
              handleCheckboxValue={handleCheckboxValue}
              errors={errors["acceptTos"]}
            />

            <p>
              {t("recaptcha_1")}&nbsp;
              <Link href={googlePrivacyUrl} underline="none" target="_blank">{t("recaptcha_2")}</Link>
              &nbsp;{t("recaptcha_3")}&nbsp;
              <Link href={googleTosUrl} underline="none" target="_blank">{t("recaptcha_4")}</Link>
              &nbsp;{t("recaptcha_5")}.
            </p>
          </Grid>

          <Grid xs={12}>
            <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha={refreshReCaptcha} />
            <LoadingButton
              loading={otpValidated.payload}
              fullWidth
              id="signup-button"
              variant="contained"
              type="submit"
              color={errorMessage ? buttonClass : "primary"}
              disabled={otpValidated.payload || !formIsValid() || token === ''}
            >
              {t("start_free_trial")} <ArrowForwardIcon />
            </LoadingButton>
          </Grid>

          <Grid xs={12}>
            <p id="generic-form-error" dangerouslySetInnerHTML={{ __html: errorMessage ?? null }} />
          </Grid>

        </Grid>
      </form>

      <Drawer
        PaperProps={
          {
            sx: {
              width: matches ? "100%" : "50%"
            },
          }
        }
        open={openOtp} anchor='right'
      >
        <Suspense>
          <OtpForm handleOpen={toggleOtpDrawer} phoneNumber={getPhoneNumber()}></OtpForm>
        </Suspense>
      </Drawer>

    </Grid>
  );
}

export default SignupForm;